import React, { useState, useEffect, useCallback } from 'react';
import { useReservationStore } from 'hooks'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
} from '@mui/material';
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { es } from "date-fns/locale";
import { CalendarArrowUp, CalendarArrowDown, Search, Loader2, CalendarRange, FileSearch } from 'lucide-react';

interface Report {
  fecha: string;
  date:
  {
    userName: string,
    userPhone: string,
    roomName: string,
    timeTable: string,
    totalPayed: string,
    orderPayment: string,
  }[];
}

interface DateRange {
  startDate: Date | null
  endDate: Date | null
}

const transformReservations = (data: any): Report[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }
  const transformed: Report[] = [];
  Object.keys(data).forEach(date => {
    transformed.push({
      fecha: date,
      date: data[date].map((reserva: any) => ({
        userName: reserva.userName,
        userPhone: reserva.userPhone,
        roomName: reserva.roomName,
        timeTable: reserva.timeTable,
        totalPayed: reserva.totalPayed,
        orderPayment: reserva.orderPayment,
      }))
    });
  });
  return transformed;
};

// Devuelve la fecha en formato YYYY-MM-DD usando las partes LOCALES (el día que el
// admin ve en el calendario), para que no haya desfase de zona horaria al enviarla.
const toYMD = (d: Date | null): string | null => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const num = (v: any) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const roomColor = (roomName: string) => {
  switch ((roomName || '').toLowerCase()) {
    case 'sala suite': return 'bg-blue-600';
    case 'sala 3': return 'bg-yellow-600';
    case 'sala 2': return 'bg-emerald-600';
    case 'sala 1': return 'bg-purple-600';
    case 'sala 4': return 'bg-amber-950';
    case 'sala 5': return 'bg-slate-300 text-black';
    default: return 'bg-gray-700';
  }
};

export function TablaReservas() {
  const { getReservationsByDates } = useReservationStore();
  const [filtro, setFiltro] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [activePreset, setActivePreset] = useState<string>('mes');
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null });

  // Lanza la búsqueda con fechas explícitas (evita depender del estado asíncrono)
  const runSearch = useCallback(async (start: Date | null, end: Date | null) => {
    if (!start || !end) {
      setError('Selecciona la fecha de inicio y la fecha final.');
      return;
    }
    if (start > end) {
      setError('La fecha de inicio no puede ser posterior a la fecha final.');
      return;
    }
    setError(null);
    setLoading(true);
    setHasSearched(true);
    setPage(0);
    try {
      const data = await getReservationsByDates(toYMD(start), toYMD(end));
      setReports(transformReservations(data));
    } catch (e) {
      console.error("Error al obtener reservas:", e);
      setError('No se pudieron cargar las reservas. Inténtalo de nuevo.');
      setReports([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Presets rápidos de rango
  const applyPreset = (preset: string) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);
    if (preset === 'hoy') {
      // start = end = hoy
    } else if (preset === 'ayer') {
      start.setDate(today.getDate() - 1);
      end.setDate(today.getDate() - 1);
    } else if (preset === '7dias') {
      start.setDate(today.getDate() - 6);
    } else if (preset === 'mes') {
      // Mes natural completo (1 -> último día), para que coincida con el dashboard.
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }
    setActivePreset(preset);
    setDateRange({ startDate: start, endDate: end });
    runSearch(start, end);
  };

  // Al entrar: cargar "Este mes" para no dejar la página vacía
  useEffect(() => {
    applyPreset('mes');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = (date: Date | null) => {
    setActivePreset('');
    setDateRange((prev) => ({ ...prev, startDate: date }));
  };
  const handleEndDateChange = (date: Date | null) => {
    setActivePreset('');
    setDateRange((prev) => ({ ...prev, endDate: date }));
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatFecha = (fecha: string) => {
    const [year, month, day] = fecha.split('-');
    return `${day}-${month}-${year}`;
  };

  // Filtrado por usuario/teléfono: filtra también las filas dentro de cada día
  const q = filtro.trim().toLowerCase();
  const reservasFiltradas = (reports || [])
    .map((reserva) => ({
      ...reserva,
      date: q
        ? reserva.date.filter(
          (e) => (e.userName || '').toLowerCase().includes(q) || (e.userPhone || '').includes(q)
        )
        : reserva.date,
    }))
    .filter((reserva) => reserva.date.length > 0);

  const getTotalReservas = () => reservasFiltradas.reduce((t, r) => t + r.date.length, 0);
  const getTotalBilled = () =>
    reservasFiltradas.reduce((t, r) => t + r.date.reduce((s, e) => s + num(e.totalPayed), 0), 0);
  const getTotalWithoutIVA = () => getTotalBilled() / 1.21;
  const getIVA = () => getTotalBilled() - getTotalWithoutIVA();
  const eur = (n: number) => n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const presets = [
    { key: 'hoy', label: 'Hoy' },
    { key: 'ayer', label: 'Ayer' },
    { key: '7dias', label: 'Últimos 7 días' },
    { key: 'mes', label: 'Este mes' },
  ];

  const dpClass =
    "w-full sm:w-[150px] rounded-lg bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-500 cursor-pointer";

  return (
    <div className="mt-8">
      {/* Presets rápidos */}
      <div className="flex flex-wrap justify-center gap-2 mb-4">
        {presets.map((p) => (
          <button
            key={p.key}
            onClick={() => applyPreset(p.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition border ${activePreset === p.key
              ? 'bg-purple-600 border-purple-500 text-white'
              : 'bg-neutral-900 border-neutral-700 text-slate-300 hover:bg-neutral-800'
              }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Controles: fechas, búsqueda y botón */}
      <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-end justify-center max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex flex-col text-xs text-slate-400 gap-1">
            <span className="flex items-center gap-1"><CalendarArrowDown className="h-3.5 w-3.5" /> Desde</span>
            <DatePicker
              selected={dateRange.startDate}
              onChange={handleStartDateChange}
              selectsStart
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              placeholderText="Fecha de inicio"
              dateFormat="dd-MM-yyyy"
              locale={es}
              className={dpClass}
            />
          </label>
          <label className="flex flex-col text-xs text-slate-400 gap-1">
            <span className="flex items-center gap-1"><CalendarArrowUp className="h-3.5 w-3.5" /> Hasta</span>
            <DatePicker
              selected={dateRange.endDate}
              onChange={handleEndDateChange}
              selectsEnd
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              minDate={dateRange.startDate}
              placeholderText="Fecha final"
              dateFormat="dd-MM-yyyy"
              locale={es}
              className={dpClass}
            />
          </label>
        </div>

        <div className="flex-1 min-w-0 flex flex-col text-xs text-slate-400 gap-1">
          <span className="flex items-center gap-1"><Search className="h-3.5 w-3.5" /> Buscar</span>
          <TextField
            placeholder="Nombre o teléfono"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#171717",
                color: "#f1f5f9",
                "& fieldset": { borderColor: "#404040" },
                "&:hover fieldset": { borderColor: "#525252" },
                "&.Mui-focused fieldset": { borderColor: "#a855f7" },
              },
              "& .MuiInputBase-input": { padding: "8px 12px", fontSize: "0.875rem" },
              "& .MuiInputBase-input::placeholder": { color: "#64748b", opacity: 1 },
            }}
          />
        </div>

        <button
          onClick={() => runSearch(dateRange.startDate, dateRange.endDate)}
          disabled={loading || !dateRange.startDate || !dateRange.endDate}
          className="h-[38px] px-6 rounded-lg uppercase text-sm font-semibold bg-purple-600 hover:bg-purple-500 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center gap-2 transition"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          Buscar
        </button>
      </div>

      {error && (
        <p className="text-center text-rose-400 text-sm mt-3">{error}</p>
      )}

      {/* Resumen */}
      {!loading && hasSearched && reservasFiltradas.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto mt-8">
          <div className="rounded-xl border border-neutral-700 bg-neutral-900 p-4">
            <p className="text-xs uppercase text-slate-400">Total facturado</p>
            <p className="text-xl font-bold text-white mt-1">{eur(getTotalBilled())}</p>
          </div>
          <div className="rounded-xl border border-neutral-700 bg-neutral-900 p-4">
            <p className="text-xs uppercase text-slate-400">Base (sin IVA)</p>
            <p className="text-xl font-bold text-white mt-1">{eur(getTotalWithoutIVA())}</p>
          </div>
          <div className="rounded-xl border border-neutral-700 bg-neutral-900 p-4">
            <p className="text-xs uppercase text-slate-400">IVA (21%)</p>
            <p className="text-xl font-bold text-white mt-1">{eur(getIVA())}</p>
          </div>
          <div className="rounded-xl border border-purple-700 bg-purple-950/40 p-4">
            <p className="text-xs uppercase text-purple-300">Nº reservas</p>
            <p className="text-xl font-bold text-white mt-1">{getTotalReservas()}</p>
          </div>
        </div>
      )}

      {/* Estados */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin mb-3" />
          <p>Cargando reservas…</p>
        </div>
      ) : !hasSearched ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <CalendarRange className="h-10 w-10 mb-3" />
          <p>Selecciona un rango de fechas y pulsa Buscar.</p>
        </div>
      ) : reservasFiltradas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <FileSearch className="h-10 w-10 mb-3" />
          <p>No hay reservas en el rango seleccionado{q ? ' para esa búsqueda' : ''}.</p>
        </div>
      ) : (
        <div className="mt-8">
          <TableContainer
            component={Paper}
            sx={{ backgroundColor: "black", border: "1px solid #404040", borderRadius: "15px", overflowX: "auto" }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  {['Fecha', 'Usuario', 'Teléfono', 'Sala', 'Horario', 'Importe', 'Estado', 'Nº Pedido'].map((h) => (
                    <TableCell key={h} sx={{ color: "white", textTransform: "uppercase", fontWeight: "bold", fontSize: "0.85rem", borderBottom: "1px solid #404040", whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reservasFiltradas
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((reserva, index) =>
                    reserva.date.map((entry, subIndex) => (
                      <TableRow key={`${index}-${subIndex}`} className="hover:bg-purple-900/40">
                        {subIndex === 0 && (
                          <TableCell
                            sx={{ color: "white", borderRight: "1px solid #404040", borderBottom: "1px solid #262626", fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top" }}
                            rowSpan={reserva.date.length}
                          >
                            {formatFecha(reserva.fecha)}
                          </TableCell>
                        )}
                        <TableCell sx={{ color: "white", borderBottom: "1px solid #262626" }}>{entry.userName}</TableCell>
                        <TableCell sx={{ color: "white", borderBottom: "1px solid #262626" }}>{entry.userPhone}</TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #262626" }}>
                          <p className={`rounded-full text-center px-2 py-1 text-white text-xs whitespace-nowrap ${roomColor(entry.roomName)}`}>
                            {entry.roomName}
                          </p>
                        </TableCell>
                        <TableCell sx={{ color: "white", borderBottom: "1px solid #262626", whiteSpace: "nowrap" }}>{entry.timeTable}</TableCell>
                        <TableCell sx={{ color: "white", borderBottom: "1px solid #262626", whiteSpace: "nowrap" }}>{num(entry.totalPayed).toFixed(2)} €</TableCell>
                        <TableCell sx={{ borderBottom: "1px solid #262626" }}>
                          <span className="rounded-full bg-emerald-600/80 text-white text-xs px-2 py-1 whitespace-nowrap">Pagado</span>
                        </TableCell>
                        <TableCell sx={{ color: "#94a3b8", borderBottom: "1px solid #262626", fontSize: "0.8rem" }}>{entry.orderPayment}</TableCell>
                      </TableRow>
                    ))
                  )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            className="mt-4"
            rowsPerPageOptions={[500, 1000, 1500]}
            component="div"
            count={reservasFiltradas.length}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Días por página"
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ color: "white", "& .MuiSvgIcon-root": { color: "white" } }}
          />
        </div>
      )}
    </div>
  );
}
